import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Sparkles } from "lucide-react";

/**
 * Modal that intercepts a structured HTTP 402 tier_limit_reached payload
 * from the backend and presents a friendly upgrade CTA instead of a
 * plain error toast.
 *
 * Expected `notice` shape (from POST /api/players when capped):
 *   { error: "tier_limit_reached", limit_name, tier, tier_name,
 *     cap, current, upgrade_to, upgrade_to_name, message }
 */
export default function TierUpgradeDialog({ notice, onClose }) {
  const open = !!notice;
  if (!notice) {
    return (
      <Dialog open={open} onOpenChange={() => onClose?.()}>
        <DialogContent />
      </Dialog>
    );
  }
  const toEnterprise = notice.upgrade_to === "enterprise";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white sm:max-w-md"
        data-testid="tier-upgrade-dialog"
      >
        <DialogHeader>
          <div className="inline-flex self-start items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.2em] font-bold uppercase mb-2"
            style={{ backgroundColor: "rgba(217,4,41,0.18)", color: "var(--brand-secondary)" }}>
            <Sparkles className="h-3 w-3" /> Time to grow
          </div>
          <DialogTitle className="text-white font-display font-black text-2xl tracking-tighter">
            You&apos;ve reached the {notice.tier_name} tier&apos;s {notice.cap} {notice.limit_name} cap.
          </DialogTitle>
          <DialogDescription className="text-white/65 mt-2 leading-relaxed">
            {notice.upgrade_to_name ? (
              <>Upgrade to the <span className="text-white font-semibold">{notice.upgrade_to_name}</span> tier to add unlimited {notice.limit_name} and unlock departmental tools — your existing data, integrations, and history carry over instantly.</>
            ) : (
              <>You&apos;re at the top of our self-serve tiers. Book an Enterprise consultation to lift this cap and unlock white-label, custom domains, and executive dashboards.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl p-4 mt-2 grid grid-cols-3 gap-3 text-center" style={{ backgroundColor: "var(--ink-1)" }}>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Current</div>
            <div className="font-display font-black text-3xl text-white font-mono-num">{notice.current}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Cap</div>
            <div className="font-display font-black text-3xl text-white font-mono-num">{notice.cap}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Tier</div>
            <div className="font-display font-black text-xl text-white mt-1">{notice.tier_name}</div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 mt-4">
          <Button variant="outline" onClick={() => onClose?.()} className="border-white/15 text-white bg-white/5 hover:bg-white/10">
            Not now
          </Button>
          <Link to={toEnterprise ? "/enterprise/contact" : "/pricing"} onClick={() => onClose?.()}>
            <Button className="text-white red-glow font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="tier-upgrade-dialog-cta">
              {toEnterprise ? "Book Enterprise consultation" : `Upgrade to ${notice.upgrade_to_name}`} <ArrowUpRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
